import { useRef, useState } from "react";
import useSWR from "swr";
import { format } from "date-fns";
import { ChevronDownIcon, ChevronRightIcon, DownloadIcon, TrashIcon, UploadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Section } from "@/components/section";
import { cn } from "@/lib/utils";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { useToast } from "@/hooks/use-toast";
import { useIsAdmin } from "@/hooks/auth";
import { toastError } from "@/lib/toast";
import { deleteFetcherKeyed, fetcher, mutateRequest, postFetcher, useAuthStore, swrKey } from "@/lib/swr";
import { DATE_FULL } from "@/lib/date";
import { ROW_CARD_STATIC_CLASS } from "@/components/list/row-card";

export interface Artifact {
  id: string;
  versionId: string;
  fileName: string;
  contentType: string;
  size: number;
  uploaderId: string | null;
  uploaderName?: string;
  /** Channel this build belongs to; null = generic build (the only kind on
   * apps without a channel manifest). */
  channelId?: number | null;
  channelName?: string;
  state: "pending" | "ready";
  createdAt: string;
}

interface Channel {
  id: number;
  name: string;
}

export const formatSize = (bytes: number) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n >= 10 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`;
};

// Grouping key for "overwrite by re-upload": same kind = newer replaces older
// in the current list. aab is the headline artifact, mapping files are common.
const artifactKind = (fileName: string) => {
  const lower = fileName.toLowerCase();
  if (lower.includes("mapping")) return "mapping";
  return lower.split(".").pop() ?? "file";
};

const KIND_ORDER = ["aab", "apk", "mapping"];
const kindRank = (kind: string) => {
  const i = KIND_ORDER.indexOf(kind);
  return i === -1 ? KIND_ORDER.length : i;
};

export interface ArtifactGroup {
  channelId: number | null;
  /** Group heading; falls back to the id when the channel row was deleted. */
  label: string;
  current: Artifact[];
  history: Artifact[];
}

// Channel apps (e.g. JPSingle) ship one flavor build per store channel, so
// "current vs history" is per (channel, kind), not just per kind. Input is
// newest-first (server order); the generic (null-channel) group comes first,
// then channels by id — stable regardless of upload order.
export const groupArtifacts = (artifacts: Artifact[]): ArtifactGroup[] => {
  const groups = new Map<number | null, ArtifactGroup>();
  const seenKinds = new Map<number | null, Set<string>>();
  for (const a of artifacts) {
    const channelId = a.channelId ?? null;
    let group = groups.get(channelId);
    if (!group) {
      group = {
        channelId,
        label:
          channelId == null
            ? "General"
            : (a.channelName ?? `Channel #${channelId}`),
        current: [],
        history: [],
      };
      groups.set(channelId, group);
      seenKinds.set(channelId, new Set());
    }
    const seen = seenKinds.get(channelId)!;
    const kind = artifactKind(a.fileName);
    if (seen.has(kind)) {
      group.history.push(a);
    } else {
      seen.add(kind);
      group.current.push(a);
    }
  }
  const list = [...groups.values()];
  for (const g of list) {
    g.current.sort(
      (a, b) =>
        kindRank(artifactKind(a.fileName)) - kindRank(artifactKind(b.fileName))
    );
  }
  list.sort((a, b) => {
    if (a.channelId == null) return -1;
    if (b.channelId == null) return 1;
    return a.channelId - b.channelId;
  });
  return list;
};

const ArtifactRow = ({
  artifact,
  onDelete,
  muted,
}: {
  artifact: Artifact;
  onDelete?: (a: Artifact) => void;
  muted?: boolean;
}) => {
  const { toast } = useToast();

  // Machine uploads (Jenkins CI) have no user identity: null uploader.
  const uploader =
    artifact.uploaderName ?? (artifact.uploaderId ? undefined : "Jenkins");

  // Signed URLs are short-lived: request one per click, then open it.
  // Safari blocks window.open called after an await (not a direct user
  // gesture anymore): open the tab synchronously, then point it at the URL.
  const download = async () => {
    const win = window.open("", "_blank");
    try {
      const res = await fetcher<{ url: string }>([
        `/versions/${artifact.versionId}/artifacts/${artifact.id}/download-url`,
      ]);
      if (win) win.location.href = res.url;
      else window.location.href = res.url; // popup blocked: same-tab download
    } catch (error) {
      win?.close();
      toastError(toast, "Download failed", error);
    }
  };

  return (
    <div
      className={cn(
        ROW_CARD_STATIC_CLASS,
        "gap-2",
        muted && "opacity-60",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium" title={artifact.fileName}>
          {artifact.fileName}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatSize(artifact.size)}
          {uploader ? ` · ${uploader}` : ""}
          {` · ${format(new Date(artifact.createdAt), DATE_FULL)}`}
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="size-7 shrink-0"
        title="Download"
        onClick={download}
      >
        <DownloadIcon />
      </Button>
      {onDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
          title="Delete"
          onClick={() => onDelete(artifact)}
        >
          <TrashIcon />
        </Button>
      )}
    </div>
  );
};

// One channel group's rows: current items, plus its own History fold. On
// non-channel apps there is a single unlabeled group — DOM identical to the
// pre-channel rendering.
const ArtifactGroupRows = ({
  group,
  showLabel,
  canDelete,
  onDelete,
}: {
  group: ArtifactGroup;
  showLabel: boolean;
  canDelete: (a: Artifact) => boolean;
  onDelete: (a: Artifact) => void;
}) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      {showLabel && (
        <div className="px-1 pt-1 text-xs font-medium text-muted-foreground">
          {group.label}
        </div>
      )}
      {group.current.map((a) => (
        <ArtifactRow
          key={a.id}
          artifact={a}
          onDelete={canDelete(a) ? onDelete : undefined}
        />
      ))}
      {group.history.length > 0 && (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 justify-start gap-1 px-2 text-xs text-muted-foreground"
            onClick={() => setHistoryOpen((v) => !v)}
          >
            {historyOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
            History ({group.history.length})
          </Button>
          {historyOpen &&
            group.history.map((a) => (
              <ArtifactRow
                key={a.id}
                artifact={a}
                muted
                onDelete={canDelete(a) ? onDelete : undefined}
              />
            ))}
        </>
      )}
    </div>
  );
};

// Build artifacts (aab / apk / mapping) for a version. Files live in a
// private GCS bucket; the browser uploads and downloads via short-lived
// signed URLs, so bytes never pass through the server:
// upload-url → PUT to GCS → commit (server verifies the object, then ready).
// Re-uploading the same kind is the overwrite path: the newest ready row per
// (channel, kind) is current, older ones collapse under History.
// On apps with a channel manifest the builds are flavor packages: rows group
// by channel, and uploads may pick the channel (General = generic build).
export const ArtifactsSection = ({
  versionId,
  pid,
}: {
  versionId: string;
  pid?: string;
}) => {
  const { toast } = useToast();
  const isAdmin = useIsAdmin();
  const userId = useAuthStore((s) => s.auth?.id);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Artifact | null>(null);
  // Channel picker for a picked file (channel apps only): "general" or an id.
  const [pendingUpload, setPendingUpload] = useState<File | null>(null);
  const [uploadChannel, setUploadChannel] = useState("general");

  const listPath = `/versions/${versionId}/artifacts`;
  const { data, isLoading } = useSWR<Artifact[]>([listPath]);
  const artifacts = data ?? [];

  // Same key as the version detail page's channels fetch, so SWR dedupes.
  const { data: channels } = useSWR<Channel[]>(
    pid ? swrKey("/channels", { pid }) : null
  );
  const hasChannels = (channels?.length ?? 0) > 0;

  const groups = groupArtifacts(artifacts);
  // Labels only when channels are in play; a plain app keeps the old look
  // (orphan channelId rows still get labels even if the manifest was emptied).
  const showLabels = hasChannels || groups.some((g) => g.channelId != null);

  const upload = async (file: File, channelId?: number) => {
    setUploading(true);
    try {
      const contentType = file.type || "application/octet-stream";
      const { artifactId, uploadUrl } = await postFetcher<{
        artifactId: string;
        uploadUrl: string;
      }>(
        `${listPath}/upload-url`,
        {
          arg: {
            fileName: file.name,
            contentType,
            size: file.size,
            ...(channelId != null ? { channelId } : {}),
          },
        }
      );
      // Direct PUT to GCS — Content-Type must match the signed value.
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });
      if (!put.ok) throw new Error(`GCS upload failed (${put.status})`);
      await postFetcher(`${listPath}/${artifactId}/commit`, { arg: {} });
      mutateRequest(listPath);
      toast({ description: `${file.name} uploaded.` });
    } catch (error) {
      toastError(toast, "Upload failed", error);
    } finally {
      setUploading(false);
    }
  };

  const onFilePicked = (file: File) => {
    if (hasChannels) {
      // Channel app: ask which channel this build belongs to first.
      setUploadChannel("general");
      setPendingUpload(file);
    } else {
      void upload(file);
    }
  };

  const remove = async (artifact: Artifact) => {
    try {
      await deleteFetcherKeyed(`${listPath}/${artifact.id}`);
      mutateRequest(listPath);
      toast({ description: `${artifact.fileName} deleted.` });
    } catch (error) {
      toastError(toast, "Delete failed", error);
    }
  };

  const canDelete = (a: Artifact) => isAdmin || a.uploaderId === userId;

  return (
    <Section
      title="Artifacts"
      action={
        <>
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) onFilePicked(file);
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            disabled={uploading}
            onClick={() => fileInput.current?.click()}
          >
            <UploadIcon />
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <ListLoading />
      ) : groups.length === 0 ? (
        <ListEmpty>No artifacts. Upload an aab / apk / mapping file.</ListEmpty>
      ) : (
        <div className="flex flex-col gap-1">
          {groups.map((g) => (
            <ArtifactGroupRows
              key={g.channelId ?? "general"}
              group={g}
              showLabel={showLabels}
              canDelete={canDelete}
              onDelete={setPendingDelete}
            />
          ))}
        </div>
      )}

      <Dialog
        open={pendingUpload !== null}
        onOpenChange={(open) => !open && setPendingUpload(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Upload artifact</DialogTitle>
            <DialogDescription>
              This app ships per-channel builds. Pick the channel{" "}
              {pendingUpload?.name} belongs to, or General for a generic build.
            </DialogDescription>
          </DialogHeader>
          <Select value={uploadChannel} onValueChange={setUploadChannel}>
            <SelectTrigger size="sm" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General</SelectItem>
              {(channels ?? []).map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPendingUpload(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={uploading}
              onClick={() => {
                const file = pendingUpload;
                setPendingUpload(null);
                if (!file) return;
                void upload(
                  file,
                  uploadChannel === "general"
                    ? undefined
                    : Number(uploadChannel)
                );
              }}
            >
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete artifact?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.fileName} will be permanently removed from
              storage. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) void remove(pendingDelete);
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
};

export default ArtifactsSection;
